/**
 * CsmManager - Manages Cascaded Shadow Maps (CSM) for the world.
 * Handles discovery of directional lights with CSM enabled, synchronizes shadow frustums,
 * and manages material setup for WebGL CSM rendering.
 */

import * as THREE from 'three';
import { CSM } from 'three/addons/csm/CSM.js';

import { DirectionalLightComponent } from '../components/lights/DirectionalLightComponent.js';

import type { IGenesysRenderer } from './IGenesysRenderer.js';
import type { World } from '../game/World.js';
import type { Material } from 'three';

/** Manages CSM shadow synchronization and material setup for a World. */
export class CsmManager {
  private _lastActiveLight: WeakRef<DirectionalLightComponent> | null = null;
  private csmMaterialSetupCache: Set<string> = new Set();
  private lastCsmSyncErrorTime: number = 0;
  private lastLightState: string | null = null;

  /** The currently active directional light with CSM enabled, or null. */
  public get lastActiveLight(): DirectionalLightComponent | null {
    return this._lastActiveLight?.deref() ?? null;
  }
  private set lastActiveLight(light: DirectionalLightComponent | null) {
    this._lastActiveLight = light ? new WeakRef(light) : null;
  }

  /** Synchronizes CSM shadows for the active directional light. Called before rendering. */
  @THREE.profile
  public tickCSM(world: World, renderer: IGenesysRenderer, camera: THREE.Camera, scene: THREE.Scene): void {
    /*
    * for the time being we traverse the scene graph in the tick to get the first light with csm shadow enabled
    * cache is NOT used because it's hard to invalidate the cache when component is added/removed dynamically
    */
    let currentLight: DirectionalLightComponent | null = null;
    for (const actor of world.getActors()) {
      for (const light of actor.getComponents(DirectionalLightComponent)) {
        // TODO: check light ancestors to determine its actual visibility
        if (light.visible && light.castShadow && light.useCsmShadows) {
          currentLight = light;
          break;
        }
      }
    }

    // detect light changes
    const previousLight = this.lastActiveLight;
    const currentState = currentLight ? currentLight.getCSMStateKey() : null;
    if (currentLight !== previousLight || currentState !== this.lastLightState) {
      if (previousLight) {
        previousLight.deactivateCSM();
      }
      if (currentLight) {
        currentLight.activateCSM(renderer, camera, scene);
      }
      this.lastActiveLight = currentLight;
      this.lastLightState = currentState;
      this.csmMaterialSetupCache.clear();
    }

    if (!currentLight) {
      return;
    }

    try {
      currentLight.tickCSM(renderer, camera, scene);
    } catch (error) {
      this.csmMaterialSetupCache.clear();

      const now = Date.now();
      if (now - this.lastCsmSyncErrorTime > 2000) {
        this.lastCsmSyncErrorTime = now;
        console.error('[CsmManager] Directional CSM sync failed. Falling back safely for this frame.', error);
      }

      return;
    }

    if (renderer.asWebGPU()) {
      this.csmMaterialSetupCache.clear();
    } else {
      // currently called in the tick to support dynamic component creation
      this.setupWebGLCsmMaterials(currentLight, scene);
    }
  }

  @THREE.profile
  private setupWebGLCsmMaterials(light: DirectionalLightComponent, scene: THREE.Scene): void {
    const csm = light.getWebGLCsm();
    if (!csm) {
      return;
    }

    scene.traverse(object => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh || !mesh.material) {
        return;
      }

      this.setupCsmMaterial(mesh.material, csm);
    });
  }

  private setupCsmMaterial(material: Material | Material[], csm: CSM): void {
    if (Array.isArray(material)) {
      for (const item of material) {
        this.setupCsmMaterial(item, csm);
      }
      return;
    }

    if (this.csmMaterialSetupCache.has(material.uuid)) {
      return;
    }

    csm.setupMaterial(material);
    this.csmMaterialSetupCache.add(material.uuid);
  }

  /** Disposes CSM resources and clears caches. Called during world destruction. */
  public dispose(): void {
    const activeLight = this.lastActiveLight;
    if (activeLight) {
      activeLight.deactivateCSM();
    }
    this.lastActiveLight = null;
    this.csmMaterialSetupCache.clear();
  }
}
